import React, { useState } from 'react';
import { tbqFunctions } from './firebase-config';
import { httpsCallable } from 'firebase/functions';

const DTQLocationForm = ({
  location,
  onClose,
  onSave,
  existingIds,
  existingCities = [],
}) => {
  const isEditing = !!location;

  const [formData, setFormData] = useState({
    id: location?.id || '',
    name: location?.name || '',
    english: location?.english || '',
    city: location?.city || '',
    otherCity: '',
    address: location?.address || '',
    mudarris: location?.mudarris || '',
    contact: location?.contact || '',
    time: location?.time || '',
    type: location?.type ?? 0,
    isLadies: location?.isLadies ?? true,
    isLadiesMessage: location?.isLadiesMessage || 'خواتین کی شرکت کا باپردہ انتظام ہے',
    lat: location?.lat || '',
    lng: location?.lng || '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const upsertDtqLocation = httpsCallable(tbqFunctions, 'upsertDtqLocation');

  const cityOptions = React.useMemo(() => {
    const citySet = new Set(existingCities.filter(Boolean));
    if (location?.city) citySet.add(location.city);
    citySet.add('Other');
    return [...citySet].sort();
  }, [existingCities, location?.city]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value === '' ? '' : Number(value),
    }));
  };

  const validate = () => {
    if (!formData.id.trim()) return 'Document ID is required';
    if (!isEditing && existingIds.includes(formData.id.trim())) return 'This ID already exists';
    if (!formData.name.trim()) return 'Name (Urdu) is required';
    const cityValue = formData.city === 'Other' ? formData.otherCity?.trim() : formData.city?.trim();
    if (!cityValue) return 'City is required (or specify under Other)';
    if (!formData.address.trim()) return 'Address is required';
    if (!formData.mudarris.trim()) return 'Mudarris is required';
    if (!formData.contact.trim()) return 'Contact is required';
    if (!formData.time.trim()) return 'Time is required';
    if (formData.lat === '' || formData.lng === '') return 'Location coordinates (lat/lng) are required';
    if (isNaN(Number(formData.lat)) || isNaN(Number(formData.lng))) return 'Coordinates must be valid numbers';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');

    try {
      const docId = formData.id.trim();
      const lat = Number(formData.lat);
      const lng = Number(formData.lng);

      const cityValue = formData.city === 'Other' ? formData.otherCity.trim() : formData.city.trim();
      const payload = {
        name: formData.name.trim(),
        city: cityValue,
        address: formData.address.trim(),
        mudarris: formData.mudarris.trim(),
        contact: formData.contact.trim(),
        time: formData.time.trim(),
        type: Number(formData.type),
        isLadies: formData.isLadies,
        isLadiesMessage: formData.isLadiesMessage.trim(),
        lat,
        lng,
      };

      // Include english name if provided
      if (formData.english.trim()) {
        payload.english = formData.english.trim();
      }

      await upsertDtqLocation({ id: docId, data: payload });
      onSave();
    } catch (err) {
      console.error("Error saving location:", err);
      const code = err?.code ? ` (${err.code})` : '';
      setError(`Failed to save${code}: ${err.message}`);
    }

    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content form-modal" onClick={e => e.stopPropagation()}>
        <div className="form-header">
          <h2>{isEditing ? 'Edit Location' : 'Add New Location'}</h2>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="form-error">{error}</div>}

        <form onSubmit={handleSubmit} className="location-form">
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="id">Document ID *</label>
              <input
                id="id"
                name="id"
                type="text"
                value={formData.id}
                onChange={handleChange}
                disabled={isEditing}
                placeholder="e.g. 715 or I4"
                required
              />
              {isEditing && <small className="field-hint">ID cannot be changed</small>}
            </div>

            <div className="form-group">
              <label htmlFor="city">City *</label>
              <select id="city" name="city" value={formData.city} onChange={handleChange} required>
                <option value="">Select City</option>
                {cityOptions.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>

            {formData.city === 'Other' && (
              <div className="form-group">
                <label htmlFor="otherCity">Specify City *</label>
                <input
                  id="otherCity"
                  name="otherCity"
                  type="text"
                  value={formData.otherCity}
                  onChange={handleChange}
                  placeholder="e.g. Rawalpindi, Sialkot"
                  required={formData.city === 'Other'}
                />
              </div>
            )}

            <div className="form-group full-width">
              <label htmlFor="name">Name (Urdu) *</label>
              <input
                id="name"
                name="name"
                type="text"
                value={formData.name}
                onChange={handleChange}
                dir="rtl"
                placeholder="مقام کا نام"
                required
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="english">English Name</label>
              <input
                id="english"
                name="english"
                type="text"
                value={formData.english}
                onChange={handleChange}
                placeholder="English name (optional)"
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="address">Address (Urdu) *</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                dir="rtl"
                placeholder="مکمل پتہ"
                rows={2}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="mudarris">Mudarris *</label>
              <input
                id="mudarris"
                name="mudarris"
                type="text"
                value={formData.mudarris}
                onChange={handleChange}
                dir="rtl"
                placeholder="جناب ..."
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="contact">Contact *</label>
              <input
                id="contact"
                name="contact"
                type="text"
                value={formData.contact}
                onChange={handleChange}
                placeholder="03xx-xxxxxxx"
                required
              />
            </div>

            <div className="form-group full-width">
              <label htmlFor="time">Time (Urdu) *</label>
              <input
                id="time"
                name="time"
                type="text"
                value={formData.time}
                onChange={handleChange}
                dir="rtl"
                placeholder="نماز عشاء کا وقت (8:00)"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="type">Type *</label>
              <select id="type" name="type" value={formData.type} onChange={handleNumberChange}>
                <option value={0}>DTQ (Regular)</option>
                <option value={1}>Summary (خلاصہ مضامین قرآن)</option>
              </select>
            </div>

            <div className="form-group checkbox-group">
              <label>
                <input
                  type="checkbox"
                  name="isLadies"
                  checked={formData.isLadies}
                  onChange={handleChange}
                />
                Ladies Arrangement
              </label>
            </div>

            {formData.isLadies && (
              <div className="form-group full-width">
                <label htmlFor="isLadiesMessage">Ladies Message (Urdu)</label>
                <input
                  id="isLadiesMessage"
                  name="isLadiesMessage"
                  type="text"
                  value={formData.isLadiesMessage}
                  onChange={handleChange}
                  dir="rtl"
                  placeholder="خواتین کی شرکت کا باپردہ انتظام ہے"
                />
              </div>
            )}

            <div className="form-section-title">Location Coordinates</div>

            <div className="form-group">
              <label htmlFor="lat">Latitude *</label>
              <input
                id="lat"
                name="lat"
                type="number"
                step="any"
                value={formData.lat}
                onChange={handleChange}
                placeholder="e.g. 24.8607"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="lng">Longitude *</label>
              <input
                id="lng"
                name="lng"
                type="number"
                step="any"
                value={formData.lng}
                onChange={handleChange}
                placeholder="e.g. 67.0011"
                required
              />
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn-save" disabled={saving}>
              {saving ? 'Saving...' : (isEditing ? 'Update Location' : 'Add Location')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DTQLocationForm;
